import React, { FC, ReactNode, useEffect, useRef, useState } from "react";
import { TimeField, TimeFieldProps } from "@mui/x-date-pickers";
import TranslationWrapper from "../../old/beheer/components/TranslationWrapper/TranslationWrapper.tsx";
import { parse } from "date-fns";
import { Fade, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { TimeMethods, TimeRange } from "@jamezz/react-components";
import { Delete } from "@mui/icons-material";
import _ from "lodash";

type TimeRangePickerProps = {
  timeRange: TimeRange;
  onChange: (timeRange: TimeRange | null) => void;
  ExtensionComponent?: ReactNode;
  hideDeleteButton?: boolean;
};

export const TimeRangePicker: FC<TimeRangePickerProps> = ({
  timeRange,
  onChange,
  ExtensionComponent,
  hideDeleteButton,
}) => {
  const startDate = parse((timeRange.start.hours % 24) + ":" + timeRange.start.minutes, "HH:mm", new Date());
  const endDate = parse((timeRange.end.hours % 24) + ":" + timeRange.end.minutes, "HH:mm", new Date());
  const stackRef = useRef<HTMLDivElement | null>(null);
  const [showDeleteButton, setShowDeleteButton] = useState(false);
  const [startTime, setStartTime] = useState<Date>(startDate);
  const [endTime, setEndTime] = useState<Date>(endDate);

  useEffect(() => {
    if (startDate.getTime() != startTime.getTime() || endDate.getTime() != endTime.getTime()) {
      if (startTime.getTime() <= endTime.getTime() || (endTime.getHours() === 0 && endTime.getMinutes() === 0)) {
        onChange({
          start: { hours: startTime.getHours(), minutes: startTime.getMinutes() },
          end: { hours: endTime.getHours(), minutes: endTime.getMinutes() },
        });
      }
    }
  }, [startTime, endTime, onChange, startDate, endDate]);

  return (
    <Stack
      direction={"row"}
      alignItems={"center"}
      onBlur={() => {
        setTimeout(() => {
          const activeElement = document.activeElement;
          if (!activeElement || !stackRef.current?.contains(activeElement)) {
            if (TimeMethods.isSame(timeRange.start, timeRange.end)) {
              onChange(null);
            }
          }
        }, 100);
      }}
      onMouseEnter={() => {
        setShowDeleteButton(true);
      }}
      onMouseLeave={() => setShowDeleteButton(false)}
    >
      <Stack ref={(ref) => (stackRef.current = ref)} gap={1} direction={"row"} alignItems={"center"} flexWrap={"wrap"}>
        <JTimeField
          format={"HH:mm"}
          shouldDisableTime={(value) => {
            return !(value.getTime() <= endTime.getTime() || (endTime.getHours() === 0 && endTime.getMinutes() === 0));
          }}
          value={startTime}
          onChange={(value) => {
            if (value && !Number.isNaN(value.getTime())) {
              startTime.setHours(value?.getHours(), value?.getMinutes());
              setStartTime(_.clone(startTime));
            }
          }}
          sx={{
            maxWidth: "80px",
          }}
        />
        <Typography variant={"body1"}>
          <TranslationWrapper tKey={"global.to"} tDefault={"To"} />
        </Typography>
        <JTimeField
          format={"HH:mm"}
          shouldDisableTime={(value) => {
            return !(startTime.getTime() <= value.getTime() || (value.getHours() === 0 && value.getMinutes() === 0));
          }}
          value={endTime}
          onChange={(value) => {
            if (value && !Number.isNaN(value.getTime())) {
              endTime.setHours(value?.getHours(), value?.getMinutes());
              setEndTime(_.clone(endTime));
            }
          }}
          sx={{
            maxWidth: "80px",
          }}
        />
        {ExtensionComponent ? ExtensionComponent : null}
      </Stack>
      <Fade in={Boolean(showDeleteButton && !hideDeleteButton)}>
        <IconButton
          onClick={() => {
            onChange(null);
          }}
        >
          <Delete />
        </IconButton>
      </Fade>
    </Stack>
  );
};

function JTimeField({ sx, ...props }: TimeFieldProps<Date>) {
  const theme = useTheme();
  return (
    <TimeField
      {...props}
      sx={_.merge(
        {
          ".MuiOutlinedInput-notchedOutline": { border: "none" },
          ".MuiInputBase-input": {
            border: "2px solid transparent",
            // borderRadius: 1,
            textAlign: "center",
            paddingLeft: 0,
            paddingRight: 0,
            "&:hover": { border: "1px solid lightgrey", borderRadius: 1 },
            "&:focus": { border: `2px solid ${theme.palette.primary.main}`, borderRadius: 1 },
          },
        },
        sx ?? {}
      )}
    />
  );
}
